import type { Alignment, StationEquation } from '@/core/alignment';
import type { AlignmentInput } from '@/graphql/graphql';

/**
 * FNV-1a 64-bit, hex. Chosen over SHA-256 because the hash is computed
 * synchronously during render (the dialog's live estimate, the dock's
 * "alignment changed" check) and `crypto.subtle.digest` is async and absent
 * from jsdom. This is a provenance fingerprint, never a security primitive.
 */
const FNV_OFFSET = 0xcbf29ce484222325n;
const FNV_PRIME = 0x100000001b3n;
const MASK64 = 0xffffffffffffffffn;

export function fnv1a64Hex(text: string): string {
  let h = FNV_OFFSET;
  for (let i = 0; i < text.length; i++) {
    h ^= BigInt(text.charCodeAt(i) & 0xff);
    h = (h * FNV_PRIME) & MASK64;
  }
  return h.toString(16).padStart(16, '0');
}

/** Quantise to 1 mm so float noise from re-tessellation doesn't change the hash. */
const q = (v: number): string => Math.round(v * 1000).toString();

export function alignmentSourceHash(a: Pick<Alignment, 'points' | 'staStart' | 'equations'>): string {
  const parts: string[] = [q(a.staStart)];
  for (const e of [...a.equations].sort((x, y) => x.staInternal - y.staInternal)) {
    parts.push(`${q(e.staInternal)}:${q(e.staAhead)}`);
  }
  parts.push('|');
  for (let i = 0; i < a.points.length; i++) parts.push(q(a.points[i]));
  return fnv1a64Hex(parts.join(','));
}

/**
 * Concrete (non-optional) shape of what `toAlignmentInput` builds. Every
 * field's type is a subtype of the generated `AlignmentInput` (Task 5) —
 * e.g. `equations`/`points` are always present, non-nullable arrays here,
 * where the GraphQL input allows them to be omitted — so a
 * `AlignmentInputPayload` value is always directly assignable to
 * `AlignmentInput` at a mutation call site without casting or remapping.
 */
export interface AlignmentInputPayload {
  name: string;
  key: string;
  layerId: string | null;
  staStart: number;
  equations: StationEquation[];
  /** Plain float64 numbers — never a typed array, never stringified (R9). */
  points: number[][];
  sourceHash: string;
}

// Compile-time-only check that the payload really is assignable to the
// generated mutation input type — if `AlignmentInput` (Task 5's codegen)
// ever grows a new required field, this line fails to compile instead of
// silently drifting. Never called.
const _assignableToAlignmentInput = (p: AlignmentInputPayload): AlignmentInput => p;
void _assignableToAlignmentInput;

export function toAlignmentInput(a: Alignment): AlignmentInputPayload {
  const points: number[][] = [];
  for (let i = 0; i + 2 < a.points.length; i += 3) {
    points.push([a.points[i], a.points[i + 1], a.points[i + 2]]);
  }
  return {
    name: a.name,
    key: a.key,
    layerId: a.layerId || null,
    staStart: a.staStart,
    equations: [...a.equations].sort((x, y) => x.staInternal - y.staInternal),
    points,
    sourceHash: alignmentSourceHash(a),
  };
}

/**
 * True when the live registry alignment still matches the one the analysis
 * ran on. An unregistered alignment (`undefined`) returns true: there is
 * nothing to contradict, and the dock has a separate "not registered" state.
 */
export function alignmentHashMatches(meta: { sourceHash: string }, live: Alignment | undefined): boolean {
  if (!live) return true;
  return alignmentSourceHash(live) === meta.sourceHash;
}
